import { DEFAULT_CATEGORY_ID } from "../../constants/category.constants";
import { DEFAULT_CLASSIFICATION_ID } from "../../constants/classification.constants";
import { Allocation } from "./allocation.class";
import { AllocationFactory } from "./allocation.factory";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { ClassificationView } from "@bitwarden/web-vault/app/models/view/classification/classification.view";
import { CategoryUtils } from "@bitwarden/web-vault/app/models/view/category/category.utils";
import { ClassificationUtils } from "@bitwarden/web-vault/app/models/view/classification/classification.utils";
import {
  AllocationAsObjectType,
  NestedAllocationsType,
} from "@bitwarden/web-vault/app/models/store/allocations/allocation.type";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";

export class AllocationUtils {
  /**
   * Function create a set of default allocations assigned
   * to the 'uncategorized' category and the 'me' classification
   */
  static createDefaultAllocations(
    amount: number = 0, // supply the quantity amount of the transaction if known
  ): Allocation[] {
    return [
      AllocationFactory.createAllocation(DEFAULT_CATEGORY_ID, DEFAULT_CLASSIFICATION_ID, amount),
    ];
  }

  /**
   * Function create a set of default allocations assigned
   * to the 'uncategorized' category and the 'me' classification
   * but returned as an object for DALI rather than an instance of Allocation class
   */
  static createDefaultAllocationAsObject(amount: number = 0): AllocationAsObjectType {
    return {
      ctId: DEFAULT_CATEGORY_ID,
      clId: DEFAULT_CLASSIFICATION_ID,
      spt: amount,
    };
  }

  /**
   * Function to create nested allocations from a flat array of allocations
   * This object is used in c_nestedAllocations on transactions and might
   * be more useful when working with the UI to set the allocations
   * @param allocations
   */
  static getNestedAllocations(allocations: Allocation[]): NestedAllocationsType {
    const nestedAllocations: NestedAllocationsType = {};
    allocations.forEach((allocation) => {
      if (!nestedAllocations[allocation.classificationLink.id]) {
        nestedAllocations[allocation.classificationLink.id] = {};
      }
      nestedAllocations[allocation.classificationLink.id][allocation.categoryLink.id] =
        allocation.split;
    });
    return nestedAllocations;
  }

  /**
   * Function to create nested allocations from a flat array of allocations but with a new total amount
   * that is split into the same ratio as the original allocations
   * This function will be useful for splitting the normalized amount and creating the normalized allocations
   * calculated property on transactions
   * @param allocations
   * @param amount
   */
  static createNestedAllocationsWithNewAmount(
    allocations: Allocation[],
    amount: number,
  ): NestedAllocationsType {
    const totalSplit = allocations.reduce((sum, allocation) => sum + allocation.split, 0);

    const nestedAllocations: NestedAllocationsType = {};
    if (totalSplit === 0) {
      // If total split is 0, return allocations with 0 split to avoid division by zero
      allocations.forEach((allocation) => {
        if (!nestedAllocations[allocation.classificationLink.id]) {
          nestedAllocations[allocation.classificationLink.id] = {};
        }
        nestedAllocations[allocation.classificationLink.id][allocation.categoryLink.id] = 0;
      });
    } else {
      allocations.forEach((allocation) => {
        if (!nestedAllocations[allocation.classificationLink.id]) {
          nestedAllocations[allocation.classificationLink.id] = {};
        }
        nestedAllocations[allocation.classificationLink.id][allocation.categoryLink.id] =
          Math.round((allocation.split / totalSplit) * amount * 100) / 100;
      });
    }

    // check for rounding errors
    const newTotal = AllocationUtils.sumNestedAllocations(nestedAllocations);

    const roundingError = amount - newTotal;
    if (Math.abs(roundingError) > 0) {
      // apply the rounding error to the first allocation found
      const firstClassId = Object.keys(nestedAllocations)[0];
      const firstCatId = Object.keys(nestedAllocations[firstClassId])[0];
      nestedAllocations[firstClassId][firstCatId] += roundingError;
    }

    return nestedAllocations;
  }

  /**
   * Will create the calculated property c_allocationsNormalized on the transaction
   * if the normalized value is available and allocations are present
   * @param transaction
   */
  static createNormalizedAllocationsOnTransaction(transaction: TransactionView) {
    if (
      transaction?.c_valuation?.normalizedValue?.amount !== null &&
      transaction?.c_valuation?.normalizedValue?.amount !== undefined &&
      transaction?.allocations
    ) {
      const normalizedAmount = transaction.c_valuation.normalizedValue.amount;
      transaction.c_allocationsNormalized = AllocationUtils.createNestedAllocationsWithNewAmount(
        transaction.allocations,
        normalizedAmount,
      );
    }
  }

  /**
   * Function to sum the total of nested allocations
   * @param nestedAllocations
   */
  static sumNestedAllocations(nestedAllocations: NestedAllocationsType): number {
    let total = 0;
    for (const allocation of Object.values(nestedAllocations)) {
      for (const amount of Object.values(allocation)) {
        total += amount;
      }
    }
    return total;
  }

  /**
   * Function to create a flat array of allocations from nested allocations
   * This function will be useful if converting from c_nestedAllocation to
   * allocations on transactions to be saved into DALI.
   * We may want to use the nestedAllocationsType when working with the UI, but
   * will need to convert back to flat array of allocations for saving.
   * @param nestedAllocations
   */
  static nestedAllocationsToAllocations(nestedAllocations: NestedAllocationsType): Allocation[] {
    const allocations: Allocation[] = [];
    for (const classId in nestedAllocations) {
      for (const catId in nestedAllocations[classId]) {
        const split = nestedAllocations[classId][catId];
        allocations.push(AllocationFactory.createAllocation(catId, classId, split));
      }
    }
    return allocations;
  }

  /**
   * Function to create allocations from categories and classifications
   * Assume that the split between them is equally distributed
   * If one of the arrays is empty, assign the default category or classification
   * We may want to use this function eventually when we are creating transactions
   * that need to use the default allocations assigned to the account once that is implemented
   * @param categories
   * @param classifications
   * @param amount - the quantity amount to be allocated betweeen the categories and classifications
   * @param roundingDefaultCat - the category ID to which any rounding overflow will be assigned (optional)
   * @param roundingDefaultClass - the classification ID to which any rounding overflow will be assigned (optional)
   */
  static createAllocationsFromCategoriesAndClassifications(
    categories: CategoryView[],
    classifications: ClassificationView[],
    amount: number = 0,
    roundingDefaultCat?: string,
    roundingDefaultClass?: string,
  ): Allocation[] {
    if (categories.length === 0) {
      categories = [CategoryUtils.getDefaultCategoryView()];
    }
    if (classifications.length === 0) {
      classifications = [ClassificationUtils.getDefaultClassificationView()];
    }

    // default rounding to the first category and classification if not provided
    if (!roundingDefaultCat) {
      roundingDefaultCat = categories[0].id;
    }
    if (!roundingDefaultClass) {
      roundingDefaultClass = classifications[0].id;
    }

    const totalSplits = categories.length * classifications.length;
    const splitAmount = Number.parseFloat((amount / totalSplits).toFixed(2));

    const remainder = amount - splitAmount * totalSplits;

    const allocations: Allocation[] = [];

    categories.forEach((cat) => {
      classifications.forEach((cls) => {
        const allocation = AllocationFactory.createAllocation(cat.id, cls.id, splitAmount);
        // assign any remainder to the specified rounding default category and classification
        if (cat.id === roundingDefaultCat && cls.id === roundingDefaultClass) {
          allocation.split += remainder;
        }
        allocations.push(allocation);
      });
    });
    return allocations;
  }
}
